import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Mic, Square, Sparkles, Loader2, FileText, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../lib/format";

export default function VoiceAgentModal({ open, onOpenChange }) {
  const { t, lang } = useI18n();
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [structured, setStructured] = useState(null);
  const [textInput, setTextInput] = useState("");
  // VAT mode for THIS recording session. ``null`` = inherit company default
  // (the prompt picks it up server-side); "included"/"excluded" force the
  // mode regardless of what the user dictates.
  const [vatModeOverride, setVatModeOverride] = useState(null);
  const [companyVatMode, setCompanyVatMode] = useState("excluded");
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  // Fetch the active company's default mode so we can show the user what
  // the LLM will do if they don't override. Cached for the modal lifetime.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/companies").then((r) => {
      if (cancelled) return;
      const list = Array.isArray(r.data) ? r.data : [];
      // First company in the list is the active one (server returns the
      // user's companies sorted). Falls back to "excluded" for safety.
      const active = list[0] || {};
      setCompanyVatMode(active.default_vat_mode || "excluded");
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTranscript("");
      setStructured(null);
      setTextInput("");
      setVatModeOverride(null);
      stopStream();
    }
    // eslint-disable-next-line
  }, [open]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("El teu navegador no suporta gravació d'àudio");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data && chunksRef.current.push(e.data);
      mr.onstop = handleStop;
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error(
          "Permís de micròfon denegat. Si estàs dins de la vista prèvia, obre l'app en una pestanya nova (icona ↗ a la dreta) i torna-ho a provar.",
          { duration: 6000 },
        );
      } else if (name === "NotFoundError") {
        toast.error("No s'ha detectat cap micròfon al dispositiu.");
      } else {
        toast.error(`No s'ha pogut activar el micròfon: ${e.message || name}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const handleStop = async () => {
    stopStream();
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    chunksRef.current = [];
    if (blob.size < 500) {
      toast.error("Gravació massa curta");
      return;
    }
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append("language", lang);
      if (vatModeOverride) fd.append("vat_mode", vatModeOverride);
      const r = await api.post("/ai/voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTranscript(r.data.transcript);
      setStructured(r.data.structured);
    } catch (e) {
      toast.error("Error processant l'àudio: " + (e.response?.data?.detail || e.message));
    } finally {
      setProcessing(false);
    }
  };

  const submitText = async () => {
    if (!textInput.trim()) return;
    setProcessing(true);
    try {
      const r = await api.post("/ai/structure-text", {
        text: textInput,
        vat_mode: vatModeOverride || undefined,
      });
      setTranscript(r.data.transcript);
      setStructured(r.data.structured);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setProcessing(false);
    }
  };

  const createDocument = async () => {
    if (!structured) return;
    try {
      // 1. Find or create client
      let clientId = null;
      if (structured.client_name) {
        const cs = await api.get("/clients", { params: { q: structured.client_name } });
        const match = cs.data.find(
          (c) => c.name.toLowerCase() === structured.client_name.toLowerCase(),
        );
        if (match) clientId = match.id;
        else {
          const created = await api.post("/clients", {
            name: structured.client_name,
            nif_cif: structured.client_nif || "",
            address: "",
            city: "",
            postal_code: "",
            country: "Espanya",
            phone: "",
            email: "",
            notes: "Creat per Agent IA de Veu",
            tags: ["IA"],
          });
          clientId = created.data.id;
        }
      }
      if (!clientId) {
        toast.error("No s'ha pogut detectar el client");
        return;
      }
      const items = (structured.items || []).map((i) => ({
        description: i.description || "",
        quantity: Number(i.quantity || 1),
        unit_price: Number(i.unit_price || 0),
        vat_rate: Number(i.vat_rate || 21),
        irpf_rate: Number(i.irpf_rate || 0),
        discount: Number(i.discount || 0),
      }));
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        client_id: clientId,
        date: today,
        items,
        notes: structured.notes || "",
        observations: "Generat per Agent IA de Veu",
        status: "draft",
      };
      if (structured.doc_type === "invoice") {
        const due = new Date();
        due.setDate(due.getDate() + 30);
        const inv = await api.post("/invoices", {
          ...payload,
          status: "pending",
          due_date: due.toISOString().slice(0, 10),
          kind: "complete",
        });
        toast.success(`Factura ${inv.data.number} creada!`);
        onOpenChange(false);
        navigate(`/app/invoices/${inv.data.id}`);
      } else {
        const q = await api.post("/quotes", payload);
        toast.success(`Pressupost ${q.data.number} creat!`);
        onOpenChange(false);
        navigate(`/app/quotes/${q.data.id}`);
      }
    } catch (e) {
      toast.error("Error creant document: " + (e.response?.data?.detail || e.message));
    }
  };

  const total = structured?.items
    ? structured.items.reduce((acc, it) => {
        const line = Number(it.quantity || 1) * Number(it.unit_price || 0);
        return acc + line * (1 + Number(it.vat_rate || 0) / 100);
      }, 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto p-5 sm:p-6 w-[calc(100vw-1rem)] sm:w-full"
        data-testid="voice-agent-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="text-blue-400" size={22} />
            <span className="gradient-text">{t("voice.title")}</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {t("voice.sub")}
          </DialogDescription>
        </DialogHeader>

        {!structured && (
          <div className="space-y-6">
            <div className="flex flex-col items-center py-6">
              <button
                type="button"
                data-testid="voice-record-btn"
                onClick={recording ? stopRecording : startRecording}
                disabled={processing}
                className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-all ${
                  recording
                    ? "bg-rose-500 pulse-ring shadow-[0_0_60px_rgba(244,63,94,0.5)]"
                    : "bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-400 hover:to-blue-600 shadow-[0_0_40px_rgba(59,130,246,0.5)]"
                }`}
              >
                {processing ? (
                  <Loader2 className="animate-spin text-white" size={36} />
                ) : recording ? (
                  <Square className="text-white" size={32} fill="white" />
                ) : (
                  <Mic className="text-white" size={36} />
                )}
              </button>
              <p className="mt-4 text-sm text-slate-400">
                {processing
                  ? t("voice.processing")
                  : recording
                  ? t("voice.stop")
                  : t("voice.start")}
              </p>

              {/* VAT mode selector — picks how dictated prices are interpreted.
                  Default = company setting; user can override per recording. */}
              {!recording && !processing && (
                <div
                  data-testid="voice-vat-mode-selector"
                  className="mt-6 flex flex-col items-center gap-2"
                >
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">
                    {lang === "es" ? "Modo IVA del documento" : "Mode IVA del document"}
                  </div>
                  <div className="inline-flex rounded-xl bg-white/5 border border-white/10 p-1">
                    {[
                      { key: null, label: lang === "es" ? "Empresa" : "Empresa" },
                      { key: "excluded", label: lang === "es" ? "+ IVA" : "+ IVA" },
                      { key: "included", label: lang === "es" ? "IVA incl." : "IVA incl." },
                    ].map((opt) => {
                      const active = vatModeOverride === opt.key;
                      return (
                        <button
                          key={opt.label}
                          type="button"
                          data-testid={`voice-vat-mode-${opt.key || "default"}`}
                          onClick={() => setVatModeOverride(opt.key)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                            active
                              ? "bg-blue-500 text-white shadow"
                              : "text-slate-300 hover:bg-white/5"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {vatModeOverride === "included"
                      ? (lang === "es" ? "Los precios que dictes serán con IVA incluido"
                                       : "Els preus que dictis seran amb IVA inclòs")
                      : vatModeOverride === "excluded"
                      ? (lang === "es" ? "Los precios que dictes serán sin IVA (se añade después)"
                                       : "Els preus que dictis seran sense IVA (s'afegeix després)")
                      : (lang === "es"
                          ? `Usar configuración de la empresa (actualmente: ${companyVatMode === "included" ? "IVA incl." : "+ IVA"})`
                          : `Fer servir la configuració de l'empresa (actualment: ${companyVatMode === "included" ? "IVA incl." : "+ IVA"})`)}
                  </div>
                </div>
              )}

              <AnimatePresence>
                {recording && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center gap-1 h-12 mt-4"
                  >
                    {[...Array(7)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="wave-bar w-1.5"
                        animate={{
                          height: ["20%", "100%", "30%", "80%", "20%"],
                        }}
                        transition={{
                          duration: 0.8 + i * 0.1,
                          repeat: Infinity,
                          delay: i * 0.05,
                        }}
                        style={{ height: "30%" }}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="text-xs text-slate-500 italic text-center px-6">
              {t("voice.example")}
            </div>

            <div className="text-[10px] text-amber-400/80 text-center px-6 -mt-2">
              ⓘ Si el micròfon no funciona, obre l&apos;app en una pestanya nova
              o quan estigui desplegada a hosys.es
            </div>

            <div className="border-t border-white/5 pt-4">
              <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">
                {t("voice.use_text")}
              </label>
              <Textarea
                data-testid="voice-text-input"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={t("voice.example").replace(/^.+?: /, "").replace(/"/g, "")}
                className="bg-[#05070D] border-white/10 min-h-[80px]"
              />
              <Button
                data-testid="voice-text-submit"
                onClick={submitText}
                disabled={!textInput.trim() || processing}
                className="mt-3 w-full bg-blue-600 hover:bg-blue-500"
              >
                <Wand2 size={16} className="mr-2" />
                {t("voice.generate")}
              </Button>
            </div>
          </div>
        )}

        {structured && (
          <div className="space-y-4" data-testid="voice-preview">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                {t("voice.transcript")}
              </div>
              <p className="text-sm text-slate-200 italic">&quot;{transcript}&quot;</p>
            </div>

            <div className="card-surface p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="text-blue-400" size={18} />
                  <span className="font-semibold">
                    {structured.doc_type === "invoice"
                      ? t("invoices.title").slice(0, -1)
                      : t("quotes.title").slice(0, -1)}{" "}
                    →{" "}
                    <span className="text-blue-300">
                      {structured.client_name || "—"}
                    </span>
                  </span>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                  IA · {Math.round((structured.confidence || 0.8) * 100)}%
                </span>
              </div>

              <div className="space-y-2">
                {(structured.items || []).map((it, i) => (
                  <div
                    key={i}
                    className="flex items-start justify-between gap-2 py-2 border-b border-white/5 last:border-0"
                  >
                    <div className="flex-1 text-sm">
                      <p className="text-slate-200">{it.description}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {it.quantity || 1} × {formatCurrency(it.unit_price, lang)} · IVA {it.vat_rate || 21}%
                      </p>
                    </div>
                    <div className="text-sm font-medium text-blue-300 whitespace-nowrap">
                      {formatCurrency(
                        Number(it.quantity || 1) * Number(it.unit_price || 0),
                        lang,
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-sm text-slate-400">{t("editor.total")} (amb IVA)</span>
                <span className="text-xl font-semibold gradient-text">
                  {formatCurrency(total, lang)}
                </span>
              </div>
            </div>

            <div className="flex gap-2 sticky bottom-0 -mx-5 sm:-mx-6 -mb-5 sm:-mb-6 px-5 sm:px-6 py-3 bg-[#0B0F19]/95 backdrop-blur-md border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => {
                  setStructured(null);
                  setTranscript("");
                  setTextInput("");
                }}
                className="border-white/10"
              >
                Tornar
              </Button>
              <Button
                data-testid="voice-create-doc"
                onClick={createDocument}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.3)]"
              >
                <Sparkles size={16} className="mr-2" />
                {t("voice.create_doc")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
